import React, { useEffect, useRef, useState } from 'react';

import { Box, useTheme } from '@mui/material';

export interface WithListObserversProps {
  children: React.ReactNode;
  wrapperClassName?: string;
}

const OVERLAY_HEIGHT = 8;

export default function WithListObservers({
  children,
  wrapperClassName,
}: WithListObserversProps) {
  const theme = useTheme();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const [topSentinelVisible, setTopSentinelVisible] = useState(true);
  const [bottomSentinelVisible, setBottomSentinelVisible] = useState(true);

  useEffect(() => {
    const root = scrollContainerRef.current;
    const topEl = topSentinelRef.current;
    const bottomEl = bottomSentinelRef.current;
    if (!root || !topEl || !bottomEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === topEl) {
            setTopSentinelVisible(entry.isIntersecting);
          } else if (entry.target === bottomEl) {
            setBottomSentinelVisible(entry.isIntersecting);
          }
        });
      },
      {
        root,
        threshold: 0,
      },
    );

    observer.observe(topEl);
    observer.observe(bottomEl);

    return () => {
      observer.unobserve(topEl);
      observer.unobserve(bottomEl);
    };
  }, []);

  const sentinelStyle: React.CSSProperties = {
    height: '4px',
    position: 'absolute',
    backgroundColor: 'transparent',
  };

  const shadowColor = theme.palette.shadow;
  const topGradient = `linear-gradient(to bottom, ${shadowColor}, transparent)`;
  const bottomGradient = `linear-gradient(to top, ${shadowColor}, transparent)`;

  const overlayBase = {
    left: 0,
    right: 0,
    height: OVERLAY_HEIGHT,
    pointerEvents: 'none' as const,
  };

  return (
    <Box position="relative" className={wrapperClassName}>
      <Box
        position="absolute"
        top={0}
        sx={{
          ...overlayBase,
          background: topGradient,
          opacity: topSentinelVisible ? 0 : 1,
          transition: 'opacity 0.4s ease',
          borderRadius: ' 0 0 4px 4px',
          zIndex: 1000,
        }}
        aria-hidden
      />
      <Box
        ref={scrollContainerRef}
        style={{
          overflowY: 'auto',
          height: '100%',
          position: 'relative',
          paddingBottom: '2px',
        }}>
        <Box ref={topSentinelRef} style={sentinelStyle} aria-hidden />
        {children}
        <Box ref={bottomSentinelRef} style={sentinelStyle} aria-hidden />
      </Box>
      <Box
        position="absolute"
        bottom={0}
        sx={{
          ...overlayBase,
          background: bottomGradient,
          opacity: bottomSentinelVisible ? 0 : 1,
          transition: 'opacity 0.2s ease',
          borderRadius: '4px 4px 0 0',
          zIndex: 1000,
        }}
        aria-hidden
      />
    </Box>
  );
}
